/**
 * Custom GNB Mobile Drawer Component
 * 
 * WithJean 스타일의 2단 슬라이드 모바일 drawer를 관리합니다.
 */

class CustomGnbMobileDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.drawer = null;
    this.currentLayer = 'main';
    this.currentSubmenu = null;
    this.scrollLocked = false;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this._closeTimeoutId = null;
    this._closeRafId = null;
  }

  connectedCallback() {
    this.details = this.querySelector('details.custom-gnb-mobile-drawer-container');
    this.drawer = this.querySelector('.custom-gnb-mobile-drawer');
    
    if (!this.details || !this.drawer) {
      console.warn('Custom GNB Mobile Drawer: Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    // Store bound references for proper cleanup in disconnectedCallback
    this._boundHandleKeyDown = this.handleKeyDown.bind(this);

    // Event listeners
    this.details.addEventListener('toggle', this.handleToggle.bind(this));

    // Submenu triggers
    const submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleSubmenuTrigger.bind(this));
    });

    // Back buttons
    const backButtons = this.querySelectorAll('[data-submenu-back]');
    backButtons.forEach(button => {
      button.addEventListener('click', this.handleBack.bind(this));
    });

    // Close buttons
    const closeButtons = this.querySelectorAll('[data-drawer-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', this.handleClose.bind(this));
    });

    // ESC key handler
    document.addEventListener('keydown', this._boundHandleKeyDown);

    // Update focusable elements
    this.updateFocusableElements();
  }

  handleToggle(event) {
    if (this.details.open) {
      this.openDrawer();
    } else {
      this.closeDrawer();
    }
  }

  openDrawer() {
    // Cancel any pending close operations to prevent race condition
    if (this._closeTimeoutId) {
      clearTimeout(this._closeTimeoutId);
      this._closeTimeoutId = null;
    }
    if (this._closeRafId) {
      cancelAnimationFrame(this._closeRafId);
      this._closeRafId = null;
    }

    // Lock body scroll
    this.lockBodyScroll();

    // Reset to main layer (this also sets ARIA states)
    this.resetToMainLayer();

    // Force reflow to ensure transition works
    requestAnimationFrame(() => {
      if (this.drawer) {
        this.drawer.offsetHeight;
        // Add menu-open class for animation
        this.details.classList.add('menu-open');
      }
    });

    // Update focusable elements
    this.updateFocusableElements();

    // Don't auto-focus first element to prevent hover-like appearance
    // Focus will be managed by user interaction
  }

  closeDrawer() {
    // Remove menu-open class to trigger closing animation
    if (this.details && this.drawer) {
      // Force reflow to ensure current state is recognized
      this.drawer.offsetHeight;

      this._closeRafId = requestAnimationFrame(() => {
        this._closeRafId = null;

        // Remove menu-open class to start closing animation
        this.details.classList.remove('menu-open');
        // Force another reflow after class removal
        this.drawer.offsetHeight;

        // Wait for animation to complete before closing
        const handleTransitionEnd = (event) => {
          if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
            // Clear fallback timeout to prevent double-execution
            if (this._closeTimeoutId) {
              clearTimeout(this._closeTimeoutId);
              this._closeTimeoutId = null;
            }
            // Guard: if drawer was reopened during animation, do nothing
            if (!this.details.classList.contains('menu-open')) {
              this.details.open = false;
            }
            this.drawer.removeEventListener('transitionend', handleTransitionEnd);
          }
        };

        this.drawer.addEventListener('transitionend', handleTransitionEnd, { once: true });

        // Fallback: if transitionend doesn't fire, close after animation duration
        this._closeTimeoutId = setTimeout(() => {
          this._closeTimeoutId = null;
          // Guard: if drawer was reopened during timeout, do nothing
          if (!this.details.classList.contains('menu-open') && this.details.open) {
            this.details.open = false;
          }
        }, 350); // Match CSS transition duration
      });
    } else {
      // Fallback: if drawer is not available, close immediately
      if (this.details) {
        this.details.open = false;
      }
    }

    // Unlock body scroll
    this.unlockBodyScroll();

    // Reset to main layer
    this.resetToMainLayer();
  }

  handleSubmenuTrigger(event) {
    const trigger = event.currentTarget;
    const submenuName = trigger.getAttribute('data-submenu-trigger');
    
    if (!submenuName) return;
    
    // Check if submenu exists
    const submenu = this.querySelector(`[data-submenu="${submenuName}"]`);
    if (!submenu) return;
    
    // Get main layer
    const mainLayer = this.querySelector('[data-layer="main"]');
    
    // Force reflow to ensure transition works
    this.drawer.offsetHeight;
    
    // Update state
    this.currentLayer = 'sub';
    this.currentSubmenu = submenuName;
    
    // Update drawer attributes
    this.drawer.setAttribute('data-current-layer', 'sub');
    this.drawer.setAttribute('data-current-submenu', submenuName);
    
    // Mark the active sub layer (CSS slides in whichever layer has this class)
    this.querySelectorAll('[data-layer="sub"]').forEach((subLayer) => {
      subLayer.classList.toggle('custom-gnb-mobile-drawer__layer--active', subLayer === submenu);
    });

    // Update ARIA: hide main layer, show submenu
    if (mainLayer) {
      mainLayer.setAttribute('aria-hidden', 'true');
    }
    submenu.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    
    // Update focusable elements
    this.updateFocusableElements();

    // Don't auto-focus first element to prevent hover-like appearance
    // Focus will be managed by user interaction
  }

  handleBack(event) {
    // Capture submenu name BEFORE reset (resetToMainLayer sets it to null)
    const submenuName = this.currentSubmenu;

    // Reset to main layer
    this.resetToMainLayer();

    // Update focusable elements
    this.updateFocusableElements();

    // Focus the trigger that opened this submenu
    if (submenuName) {
      const trigger = this.querySelector(`[data-submenu-trigger="${submenuName}"]`);
      if (trigger) {
        setTimeout(() => {
          trigger.focus();
        }, 100);
      }
    }
  }

  handleClose(event) {
    // Use closeDrawer to ensure animation
    this.closeDrawer();
  }

  handleKeyDown(event) {
    // ESC key: close drawer
    if (event.key === 'Escape' && this.details.open) {
      event.preventDefault();
      this.handleClose(event);
      return;
    }
    
    // Tab key: trap focus within drawer
    if (event.key === 'Tab' && this.details.open) {
      this.trapFocus(event);
    }
  }

  resetToMainLayer() {
    // Force reflow to ensure transition works
    if (this.drawer) {
      this.drawer.offsetHeight;
    }
    
    this.currentLayer = 'main';
    this.currentSubmenu = null;
    
    if (this.drawer) {
      this.drawer.setAttribute('data-current-layer', 'main');
      this.drawer.removeAttribute('data-current-submenu');
    }
    
    // Update ARIA: show main layer, hide all submenus
    const mainLayer = this.querySelector('[data-layer="main"]');
    const subLayers = this.querySelectorAll('[data-layer="sub"]');
    
    if (mainLayer) {
      mainLayer.setAttribute('aria-hidden', 'false');
    }
    subLayers.forEach(subLayer => {
      subLayer.setAttribute('aria-hidden', 'true');
      subLayer.classList.remove('custom-gnb-mobile-drawer__layer--active');
    });
    
    // Reset all ARIA expanded states
    const triggers = this.querySelectorAll('[data-submenu-trigger]');
    triggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  updateFocusableElements() {
    const currentLayer = this.currentLayer === 'sub' && this.currentSubmenu
      ? this.querySelector(`[data-submenu="${this.currentSubmenu}"]`)
      : this.querySelector('[data-layer="main"]');
    
    if (!currentLayer) return;
    
    // Get all focusable elements within current layer
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    this.focusableElements = Array.from(currentLayer.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Filter out hidden elements
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      });
    
    // Also include close button
    const closeButton = this.querySelector('.custom-gnb-mobile-drawer__close-button');
    if (closeButton && !this.focusableElements.includes(closeButton)) {
      this.focusableElements.push(closeButton);
    }
    
    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null;
  }

  trapFocus(event) {
    if (!this.firstFocusableElement || !this.lastFocusableElement) {
      return;
    }
    
    const activeElement = document.activeElement;
    const isTabForward = !event.shiftKey;
    
    // If focus is outside drawer, focus first element
    if (!this.drawer.contains(activeElement)) {
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }
    
    // If at first element and Shift+Tab, wrap to last
    if (activeElement === this.firstFocusableElement && !isTabForward) {
      event.preventDefault();
      this.lastFocusableElement.focus();
      return;
    }
    
    // If at last element and Tab, wrap to first
    if (activeElement === this.lastFocusableElement && isTabForward) {
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }
  }

  lockBodyScroll() {
    if (this.scrollLocked) return;
    
    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    // Lock scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    this.scrollLocked = true;
  }

  unlockBodyScroll() {
    if (!this.scrollLocked) return;
    
    // Unlock scroll
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
    
    this.scrollLocked = false;
  }

  disconnectedCallback() {
    // Cleanup: unlock scroll if still locked
    if (this.scrollLocked) {
      this.unlockBodyScroll();
    }
    
    // Remove event listeners (use stored bound reference)
    if (this._boundHandleKeyDown) {
      document.removeEventListener('keydown', this._boundHandleKeyDown);
    }
  }
}

// Register custom element
if (!customElements.get('custom-gnb-mobile-drawer')) {
  customElements.define('custom-gnb-mobile-drawer', CustomGnbMobileDrawer);
}
